/**
 * Control bar uses `--rtk-colors-brand-400` / `500`; `rtk-button` (e.g. Join) uses `500` (fill),
 * `600` (:hover), `700` (:active) (`rtk-button.css`). Setting only 400/500 leaves hover/active on
 * mismatched defaults. Map the full ramp used by primary buttons to `buttonBackground`.
 */

import { mixHex, normalizeThemeHex } from './wcagContrast';

/** Slightly darker than base so hover/active read like RTK’s default blue ramp. */
const JOIN_BRAND_HOVER_MIX = 0.09;
const JOIN_BRAND_ACTIVE_MIX = 0.17;

/** @param {string | null | undefined} value */
function normalizeHex(value) {
  if (!value || typeof value !== 'string') return null;
  let h = value.trim();
  if (!h.startsWith('#')) h = `#${h}`;
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h)) return null;
  if (h.length === 4) {
    const [, r, g, b] = h;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return h.toLowerCase();
}

/**
 * @param {string | null | undefined} hex
 * @returns {string | null} space-separated `r g b` for `--rtk-colors-brand-*`
 */
export function hexToBrandRgbString(hex) {
  const h = normalizeHex(hex);
  if (!h) return null;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return `${r} ${g} ${b}`;
}

const BUTTON_BRAND_SHADES = ['300', '400', '500', '600', '700', '800'];

/**
 * Sets `--rtk-colors-brand-300`…`800` from a theme button fill hex (flat ramp for RTK buttons).
 *
 * @param {HTMLElement | null} hostEl
 * @param {string | null | undefined} buttonBackgroundHex — API `buttonBackground`
 */
export function applyButtonBrandRampToHost(hostEl, buttonBackgroundHex) {
  if (!hostEl || typeof document === 'undefined') return;

  const rgb = hexToBrandRgbString(buttonBackgroundHex);
  if (!rgb) return;

  for (const shade of BUTTON_BRAND_SHADES) {
    hostEl.style.setProperty(`--rtk-colors-brand-${shade}`, rgb);
  }
}

/**
 * Same tokens as {@link applyButtonBrandRampToHost}, but `600`/`700` are darkened from the base so
 * Join (`rtk-button` primary) hover/active states match component CSS instead of looking flat.
 *
 * @param {HTMLElement | null} hostEl
 * @param {string | null | undefined} buttonFillHex — resolved fill (`buttonBackground` ± tint)
 */
export function applyJoinButtonBrandRampToHost(hostEl, buttonFillHex) {
  if (!hostEl || typeof document === 'undefined') return;

  const base = normalizeThemeHex(buttonFillHex);
  if (!base) return;

  const rgbBase = hexToBrandRgbString(base);
  if (!rgbBase) return;

  const hoverHex = mixHex(base, '#000000', JOIN_BRAND_HOVER_MIX);
  const activeHex = mixHex(base, '#000000', JOIN_BRAND_ACTIVE_MIX);
  const rgb600 = hoverHex ? hexToBrandRgbString(hoverHex) : rgbBase;
  const rgb700 = activeHex ? hexToBrandRgbString(activeHex) : rgbBase;

  for (const shade of ['300', '400', '500', '800']) {
    hostEl.style.setProperty(`--rtk-colors-brand-${shade}`, rgbBase);
  }
  hostEl.style.setProperty('--rtk-colors-brand-600', rgb600 ?? rgbBase);
  hostEl.style.setProperty('--rtk-colors-brand-700', rgb700 ?? rgbBase);
}

/**
 * @param {HTMLElement | null} meetingHostEl outer `<rtk-meeting>` (walks nested inner hosts)
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function applyRtkBrandTokensForFocusRing(meetingHostEl, themeDetails) {
  if (!meetingHostEl || typeof document === 'undefined') return;

  const hex =
    themeDetails?.buttonBackground ?? themeDetails?.presetTheme?.buttonBackground ?? null;

  function walk(host) {
    if (!host) return;
    applyButtonBrandRampToHost(host, hex);
    const inner = host.shadowRoot?.querySelector('rtk-meeting');
    if (inner) walk(inner);
  }

  walk(meetingHostEl);
}
